import { useEffect, useState } from 'react'
import { BackHandler } from 'react-native'
import { useToastController } from '@my/ui'
import { uploadImage } from 'app/lib/upload-image'
import { AuthUser, useAuth } from 'app/provider/auth'
import { Group } from 'app/types'
import { createCustomUuid } from 'app/utils/create-custom-uuid'
import { GroupPostCreationWizard } from './group-post-creation-wizard'
import { groupPostsQuery } from '../../screens/group-screen/queries'
import { useCreateAnnouncement, useCreateEvent, useCreatePost } from './commands'
import { canInteract } from '../../helpers'
import { useUserGroupsQuery, userGroupsQuery } from '../../queries'
import { SubmitPostData } from './post/post-creation-view'
import { SubmitEventData } from './event/event-creation-view'
import { concatDateAndTime } from './helpers'
import { userFeedQuery } from '../../screens/groups-feed-screen/queries'
import { SubmitAnnouncementData } from './announcement/announcement-creation-view'
import { myGroupPostsQuery } from '../../screens/your-posts-screen/queries'

type GroupPostCreationWizardProps = {
  open: boolean
  onOpenChange: (value) => void
  user: Pick<AuthUser, 'id' | 'fullName' | 'avatar' | 'token'>
  defaultGroup: Pick<Group, 'id' | 'name'> | null
}

export const GroupPostCreationWizardContainer = (props: GroupPostCreationWizardProps) => {
  const { user } = useAuth()
  const toast = useToastController()
  const [submitting, setSubmitting] = useState(false)

  const [createPost] = useCreatePost()
  const [createEvent] = useCreateEvent()
  const [createAnnouncement] = useCreateAnnouncement()

  const { data: groupData, loading: userGroupsLoading } = useUserGroupsQuery({
    variables: {
      ...userGroupsQuery.defaultVariables,
    },
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    const backAction = () => {
      props.onOpenChange(false);
      return props.open
    }

    const backHandler = BackHandler.addEventListener(
      "hardwareBackPress",
      backAction
    );

    return () => backHandler.remove();
  }, [])

  const handleSubmitEvent = async (data: SubmitEventData, cb: () => void) => {
    const { name, description, image, startDate, endDate, endTime, startTime, location } = data
    if (!props.user || !data.group?.id) {
      toast.show("User or group doesn't exist", {
        burntOptions: {
          preset: 'error',
        },
      })
      return
    }

    setSubmitting(true)

    let imageId: string | null = null
    if (image) {
      const imageName = createCustomUuid()
      const imageRes = await uploadImage({
        image: image,
        name: `post-image-${data.group?.id}-${props.user.id}--${imageName}`,
        authToken: props.user.token,
        collection: 'media',
      })
      if (imageRes.doc) {
        imageId = imageRes.doc.id
      } else {
        toast.show('Failed to upload image', {
          burntOptions: {
            preset: 'error',
          },
        })
      }
    }

    const startDateTime = concatDateAndTime(new Date(startDate), startTime)
    const endDateTime = endDate ? concatDateAndTime(new Date(endDate), endTime ?? '00:00') : null

    // Create event
    await createEvent({
      variables: {
        groupId: data.group.id,
        name,
        description,
        location,
        startDate: startDateTime.toISOString(),
        ...(endDateTime && { endDate: endDateTime.toISOString() }),
        ...(imageId && { image: imageId }),
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: groupPostsQuery.query,
          variables: {
            ...groupPostsQuery.defaultVariables,
            id: data.group.id,
          },
        },
        {
          query: userFeedQuery.query,
          variables: {
            ...userFeedQuery.defaultVariables,
          },
        },
        {
          query: myGroupPostsQuery.query,
          variables: {
            ...myGroupPostsQuery.defaultVariables,
            filter: 'events',
          },
        },
      ],
      onCompleted: async (data) => {
        if (!data?.createEvent?.post.id) {
          toast.show('Failed to create the event', {
            burntOptions: {
              preset: 'error',
            },
          })
          return
        }
        setSubmitting(false)
        props.onOpenChange(false)
        toast.show('Event was created successfully')
        cb()
      },

      onError: (error) => {
        // Check if we tried to add inapropriate content
        const contentError = error.graphQLErrors.find((error) =>
          error.message.includes('banned content')
        )
        if (contentError) {
          toast.show('This event does not meet community guidelines.', {
            burntOptions: {
              preset: 'error',
            },
          })
          setSubmitting(false)
          return
        }

        toast.show('Failed to create the post', { burntOptions: { preset: 'error' } })
        setSubmitting(false)
      },
    })
  }

  const handleSubmitAnnouncement = async (data: SubmitAnnouncementData, cb: () => void) => {
    const { title, body, scheduledDate, scheduledTime } = data
    const scheduledDateTime =
      scheduledDate && scheduledTime
        ? concatDateAndTime(new Date(scheduledDate), scheduledTime)
        : null

    if (!props.user || !data.group?.id) {
      toast.show("User or group doesn't exist", {
        burntOptions: {
          preset: 'error',
        },
      })
      return
    }

    setSubmitting(true)
    await createAnnouncement({
      variables: {
        groupId: data.group.id,
        title,
        body,
        ...(scheduledDateTime && { scheduledDate: scheduledDateTime.toISOString() }),
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: groupPostsQuery.query,
          variables: {
            ...groupPostsQuery.defaultVariables,
            id: data.group.id,
          },
        },
        {
          query: userFeedQuery.query,
          variables: {
            ...userFeedQuery.defaultVariables,
          },
        },
        {
          query: myGroupPostsQuery.query,
          variables: {
            ...myGroupPostsQuery.defaultVariables,
            filter: scheduledDateTime ? 'scheduled' : 'announcements',
          },
        },
      ],
      onCompleted: async (data) => {
        if (!data?.createAnnouncement?.post.id) {
          toast.show('Failed to create the announcement', {
            burntOptions: {
              preset: 'error',
            },
          })
          return
        }
        setSubmitting(false)
        props.onOpenChange(false)
        toast.show('Announcement was created successfully')
        cb()
      },

      onError: (error) => {
        // Check if we tried to add inapropriate content
        const contentError = error.graphQLErrors.find((error) =>
          error.message.includes('banned content')
        )
        if (contentError) {
          toast.show('This announcement does not meet community guidelines.', {
            burntOptions: {
              preset: 'error',
            },
          })
          setSubmitting(false)
          return
        }

        toast.show('Failed to create the announcement', { burntOptions: { preset: 'error' } })
        setSubmitting(false)
      },
    })
  }

  const handleSubmitPost = async (data: SubmitPostData, cb: () => void) => {
    const { text, image } = data

    // TODO:
    // Support multiple images exists,
    // Just need to update the frontend
    const images = image && [image]

    if (!props.user || !data.group?.id) {
      toast.show("User or group doesn't exist", {
        burntOptions: {
          preset: 'error',
        },
      })
      return
    }

    setSubmitting(true)
    try {
      // Upload images
      let imageIds: string[] = []
      if (images && images.length !== 0) {
        for (const image of images) {
          const imageName = createCustomUuid()
          const res = await uploadImage({
            image: image,
            name: `post-image-${data.group?.id}-${props.user.id}--${imageName}`,
            authToken: props.user.token,
            collection: 'media',
          })

          if (res.doc) {
            imageIds.push(res.doc.id)
          } else {
            toast.show('Failed to upload image', {
              burntOptions: {
                preset: 'error',
              },
            })
          }
        }
      }

      // Create post
      await createPost({
        variables: {
          group: data.group.id,
          text: text || '',
          imageIds,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: groupPostsQuery.query,
            variables: {
              ...groupPostsQuery.defaultVariables,
              id: data.group.id,
            },
          },
          {
            query: userFeedQuery.query,
            variables: {
              ...userFeedQuery.defaultVariables,
            },
          },
          {
            query: myGroupPostsQuery.query,
            variables: {
              ...myGroupPostsQuery.defaultVariables,
              filter: 'posts',
            },
          },
        ],
        onCompleted: async (data) => {
          if (!data?.createPost?.post.id) {
            toast.show('Failed to create the post', {
              burntOptions: {
                preset: 'error',
              },
            })
            return
          }
          setSubmitting(false)
          props.onOpenChange(false)
          toast.show('Post was created successfully')
          cb()
        },

        onError: (error) => {
          // Check if we tried to add inapropriate content
          const contentError = error.graphQLErrors.find((error) =>
            error.message.includes('banned content')
          )
          if (contentError) {
            toast.show('This post does not meet community guidelines.', {
              burntOptions: {
                preset: 'error',
              },
            })
            setSubmitting(false)
            return
          }

          toast.show('Failed to create the post', { burntOptions: { preset: 'error' } })
          setSubmitting(false)
        },
      })
    } catch (error) {
      toast.show('Failed to create the post', {
        burntOptions: {
          preset: 'error',
        },
      })
      console.log(error)
      setSubmitting(false)
    }
  }

  const groups = (groupData?.userGroups?.docs || []).filter((userGroup) =>
    canInteract({ user, group: userGroup.group })
  )

  return (
    <GroupPostCreationWizard
      user={props.user}
      open={props.open}
      loading={userGroupsLoading}
      submitting={submitting}
      groups={groups}
      defaultGroup={props.defaultGroup}
      onOpenChange={props.onOpenChange}
      onSubmitPost={handleSubmitPost}
      onSubmitEvent={handleSubmitEvent}
      onSubmitAnnouncement={handleSubmitAnnouncement}
    />
  )
}
